/**
 * SortableCategoryList — outer dnd-kit harness for the categories edit
 * mode. Owns the `<DndContext>`, sensors, and per-parent
 * `<SortableContext>` islands.
 *
 * Decisions wired here:
 *   - Decision: Cross-parent block — separate SortableContext per parent
 *     group. We render ONE SortableContext for the main list (items =
 *     main ids) and ONE SortableContext per main category for its
 *     sub-children. dnd-kit's KeyboardSensor + collision detection are
 *     bounded by SortableContext, which is what enforces "cannot escape
 *     parent" for keyboard moves.
 *   - Decision: Touch sensor split — PointerSensor with distance: 8 and
 *     TouchSensor with delay: 150, tolerance: 6. Listeners are bound on
 *     the handle (in `SortableCategoryRow`), not the row body, so list
 *     scrolling is preserved.
 *   - Decision: Keyboard accessibility — KeyboardSensor +
 *     `sortableKeyboardCoordinates`. Tab to handle, Space to pickup,
 *     Arrow to move, Space to drop, Esc to cancel.
 *
 * Spec rules enforced in onDragEnd / onDragOver:
 *   - Drag-to-Reorder Categories Within Same Parent Group: same
 *     parent_id required for source & target; otherwise return without
 *     reorder or sync.
 *   - Sparse Integer Sort Order Recompute on Drop: delegates to
 *     `computeReorder`. Same-index drop returns empty changes → no
 *     sync emitted.
 *   - Optimistic Local Reorder With OCC Conflict Path: caller's
 *     `onReorder` is responsible for both the optimistic local update
 *     and per-row enqueueOp; this component just resolves the geometry
 *     and forwards the change list.
 */

import { useMemo, useState, type ReactNode } from 'react';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

import type { CategoryRow } from '@/lib/sheets/types';
import { computeReorder, type ReorderChange } from './sortOrderMath';

const SORT_STEP = 100;

/**
 * Pure helper extracted from `onDragEnd` so the cross-parent block +
 * sparse-integer recompute can be unit-tested without a real dnd-kit
 * harness (jsdom + dnd-kit pointer/keyboard events are unreliable —
 * see design Risks/Trade-offs).
 *
 * Returns `null` when the drop should be ignored (cross-parent move,
 * unknown ids, missing group). Returns `{ parentId, changes: [] }`
 * when the drop is in-bounds but produces no sort_order changes
 * (same-index drop) — callers MUST NOT emit sync ops in that case.
 */
export function resolveReorder(
  groups: ReadonlyArray<SortableGroup>,
  sourceId: string,
  targetId: string,
  step: number = SORT_STEP,
): { parentId: string; changes: ReorderChange[] } | null {
  const parentIdById = new Map<string, string>();
  for (const g of groups) {
    for (const r of g.rows) parentIdById.set(r.id, g.parentId);
  }
  const sourceParent = parentIdById.get(sourceId);
  const targetParent = parentIdById.get(targetId);
  if (
    sourceParent === undefined ||
    targetParent === undefined ||
    sourceParent !== targetParent
  ) {
    return null;
  }
  const group = groups.find((g) => g.parentId === sourceParent);
  if (!group) return null;
  const ids = group.rows.map((r) => r.id);
  const fromIndex = ids.indexOf(sourceId);
  const toIndex = ids.indexOf(targetId);
  if (fromIndex === -1 || toIndex === -1) return null;
  const result = computeReorder({ ids, fromIndex, toIndex, step });
  return { parentId: sourceParent, changes: [...result.changes] };
}

export interface SortableGroup {
  /** parent_id of this group ('' for the main list, sub parent id for
   *  sub groups). */
  parentId: string;
  /** Ordered category rows belonging to this group, sorted by
   *  sort_order ascending (caller responsibility). */
  rows: CategoryRow[];
  /** Renders one row given its category. The renderer decides whether
   *  the row should be greyed-out (caller looks at `groupDisabled`). */
  renderRow: (category: CategoryRow, groupDisabled: boolean) => ReactNode;
  /** Optional ReactNode to render before the rows (e.g., a parent main
   *  category header for the sub-group case). */
  header?: ReactNode;
  /** Optional ReactNode to render after the rows. */
  footer?: ReactNode;
}

export interface SortableCategoryListProps {
  groups: SortableGroup[];
  /** Called after a successful in-group drop. The callback is
   *  responsible for optimistic local update + sync enqueue. */
  onReorder: (parentId: string, changes: ReorderChange[]) => void;
}

interface ActiveDrag {
  id: string;
  parentId: string;
}

export default function SortableCategoryList({
  groups,
  onReorder,
}: SortableCategoryListProps): JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const [active, setActive] = useState<ActiveDrag | null>(null);

  // id → parentId index (for cheap O(1) lookup in onDragStart/onDragEnd).
  const parentIdById = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) {
      for (const r of g.rows) {
        map.set(r.id, g.parentId);
      }
    }
    return map;
  }, [groups]);

  const onDragStart = (event: DragStartEvent): void => {
    const id = String(event.active.id);
    const parentId = parentIdById.get(id);
    if (parentId === undefined) return;
    setActive({ id, parentId });
  };

  const onDragCancel = (): void => {
    setActive(null);
  };

  const onDragEnd = (event: DragEndEvent): void => {
    const { active: dragActive, over } = event;
    setActive(null);
    if (!over) return;

    const resolved = resolveReorder(
      groups,
      String(dragActive.id),
      String(over.id),
      SORT_STEP,
    );
    if (resolved === null) return; // cross-parent or unknown — ignore.
    if (resolved.changes.length === 0) return; // same-index = no sync op.
    onReorder(resolved.parentId, resolved.changes);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={onDragStart}
      onDragCancel={onDragCancel}
      onDragEnd={onDragEnd}
    >
      {groups.map((group) => {
        const groupDisabled = active !== null && active.parentId !== group.parentId;
        return (
          <SortableContext
            key={`group-${group.parentId}`}
            items={group.rows.map((r) => r.id)}
            strategy={verticalListSortingStrategy}
          >
            {group.header}
            {group.rows.map((row) => (
              <span key={row.id}>{group.renderRow(row, groupDisabled)}</span>
            ))}
            {group.footer}
          </SortableContext>
        );
      })}
    </DndContext>
  );
}
